import { Button } from "@/components/ui/Button/Button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  getRecencyCutoff,
  isRecentRotation,
  isRecentSession,
} from "@/utils/rotations/recency";
import { format } from "date-fns";
import { MousePointerClick, Sparkles } from "lucide-react";
import { useMemo } from "react";

const EmptySelection = ({
  data,
  selected,
  setSelected,
  getPreferences,
}: {
  data: { rotations: any[]; sessions: any[] };
  selected: { rotation?: string; session?: string };
  setSelected: ({
    rotation,
    session,
  }: {
    rotation?: string;
    session?: string;
  }) => void;
  getPreferences: () => void;
}) => {
  const cutoff = useMemo(() => getRecencyCutoff(), []);

  const recentRotations = useMemo(
    () => data.rotations.filter((r) => isRecentRotation(r, cutoff)),
    [data.rotations, cutoff],
  );

  const rotationSessions = useMemo(() => {
    if (!selected.rotation) return [];
    return data.sessions.filter(
      (s) =>
        s.rotation === selected.rotation &&
        s.singleton === false &&
        isRecentSession(s, data.rotations, cutoff),
    );
  }, [selected.rotation, data.sessions, data.rotations, cutoff]);

  return (
    <Empty style={{ borderColor: "var(--secondary-border)" }}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <MousePointerClick />
        </EmptyMedia>
        <EmptyTitle>No Session Selected</EmptyTitle>
        <EmptyDescription>
          Select a rotation and one of it's sessions to assign preferences.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Select
          value={selected.rotation}
          onValueChange={(v) =>
            setSelected({ rotation: v, session: undefined })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a rotation" />
          </SelectTrigger>
          <SelectContent>
            {recentRotations.map((r) => (
              <SelectItem key={r._id} value={r._id}>
                {format(r.date, "MMMM dd, yyyy")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selected.rotation && (
          <Select
            value={selected.session}
            onValueChange={(v) =>
              setSelected({ rotation: selected.rotation, session: v })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a session" />
            </SelectTrigger>
            <SelectContent>
              {rotationSessions.length > 0 ? (
                rotationSessions.map((s) => (
                  <SelectItem key={s._id} value={s._id}>
                    {s.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value=" ">
                  No multi-week sessions found
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        {selected.session && (
          <Button style={{ width: "100%" }} onClick={getPreferences}>
            <Sparkles />
            Find Preferences
          </Button>
        )}
      </EmptyContent>
    </Empty>
  );
};

export { EmptySelection };
